import { DEFAULT_LIVE_VOICE, LANGUAGES, LANGUAGE_CODES, LIVE_VOICES, defaultApplySettings, type Competency, type Fact, type JobInput, type KnockoutRule, type LanguageCode } from '@eduvy/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { Icon } from '../components/icons';
import { Badge, Button, Callout, Checkbox, Chip, CopyButton, ErrorNote, Field, Input, Loading, NumberField, Options, PageHeader, Section, Segmented, Select, Switch, Textarea, cx } from '../components/ui';
import { useAuth } from '../lib/auth';
import { api, type Job } from '../lib/api';
import { useScrollSpy } from '../lib/hooks';
import { AssessmentCard, ItemCard } from './AssessmentEditor';
import { JobMessagesCard } from './JobMessagesCard';
import type { JobDraftState } from './NewJob';

const OPS: Array<{ value: KnockoutRule['op']; label: string; needsValue: boolean }> = [
  { value: 'gt', label: 'is more than', needsValue: true },
  { value: 'gte', label: 'is at least', needsValue: true },
  { value: 'lt', label: 'is less than', needsValue: true },
  { value: 'lte', label: 'is at most', needsValue: true },
  { value: 'eq', label: 'equals', needsValue: true },
  { value: 'neq', label: 'does not equal', needsValue: true },
  { value: 'is_true', label: 'is yes', needsValue: false },
  { value: 'is_false', label: 'is no', needsValue: false },
  { value: 'contains', label: 'mentions', needsValue: true },
  { value: 'not_contains', label: 'does not mention', needsValue: true },
];

const SECTIONS = [
  { id: 'role', label: 'Role' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'interviewer', label: 'Interviewer' },
  { id: 'assessment', label: 'Skills assessment' },
  { id: 'competencies', label: 'Competencies' },
  { id: 'facts', label: 'Facts' },
  { id: 'rules', label: 'Qualification rules' },
  { id: 'emails', label: 'Emails' },
];

/** Brand ramp for the weight meter, one shade per competency. */
const RAMP = ['#d6217f', '#c4248a', '#a8279a', '#92299f', '#7e2ea6', '#6a2f9c', '#563190', '#45307f'];

function toKey(label: string): string {
  const k = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(k) ? k : `x_${k}`;
}

function jobToInput(job: Job): JobInput {
  return {
    title: job.title,
    department: job.department,
    location: job.location,
    workMode: job.workMode as JobInput['workMode'],
    description: job.description,
    status: job.status,
    outreachMode: job.outreachMode,
    interviewMinutes: job.interviewMinutes,
    maxCallAttempts: job.maxCallAttempts,
    retryAfterMinutes: job.retryAfterMinutes,
    linkValidityHours: job.linkValidityHours,
    config: job.config,
  };
}

export function JobEditorPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  // A new job arrives from the new-job page as a draft (AI or template) to review before it is saved.
  const drafted = (useLocation().state as JobDraftState | null) ?? null;
  const source = useQuery({
    queryKey: ['job', jobId],
    queryFn: async () => {
      const job = await api.get<Job>(`/api/jobs/${jobId}`);
      return { input: jobToInput(job), applyUrl: job.applyUrl };
    },
    enabled: Boolean(jobId),
  });
  const [form, setForm] = useState<JobInput | null>(null);
  const [initial, setInitial] = useState<string>('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const active = useScrollSpy(SECTIONS.map((s) => s.id));

  useEffect(() => {
    if (form) return;
    const start = jobId ? source.data?.input : drafted?.draft;
    if (start) {
      const copy = structuredClone(start);
      setForm(copy);
      setInitial(JSON.stringify(copy));
    }
  }, [source.data, form, jobId, drafted]);

  const dirty = useMemo(() => (form ? JSON.stringify(form) !== initial : false), [form, initial]);

  if (!jobId && !drafted?.draft) return <Navigate to="/jobs/new" replace />;
  if (!form) return source.error ? <ErrorNote error={source.error} /> : <Loading />;

  const set = <K extends keyof JobInput>(k: K, v: JobInput[K]) => setForm({ ...form, [k]: v });
  const cfg = form.config;
  const setCfg = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) => setForm({ ...form, config: { ...cfg, [k]: v } });
  const totalWeight = cfg.competencies.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  const usesLive = cfg.voiceEngine === 'gemini-live' || cfg.assessment?.voiceEngine === 'gemini-live';
  const apply = cfg.apply ?? defaultApplySettings();
  const setApply = (patch: Partial<typeof apply>) => setCfg('apply', { ...apply, ...patch });
  const savedApply = jobId ? (JSON.parse(initial || '{}') as JobInput).config?.apply : undefined;
  // The link is the server's: it is right for the saved version, and changes once a new version is saved.
  const applyLink = jobId && source.data?.applyUrl && savedApply?.enabled && savedApply.version === apply.version ? source.data.applyUrl : null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = jobId ? await api.put<{ id: string }>(`/api/jobs/${jobId}`, form) : await api.post<{ id: string }>('/api/jobs', form);
      await qc.invalidateQueries({ queryKey: ['jobs'] });
      await qc.invalidateQueries({ queryKey: ['job', saved.id] });
      navigate(`/jobs/${saved.id}`);
    } catch (err) {
      setError(err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const updateList = <T,>(list: T[], i: number, patch: Partial<T>): T[] => list.map((x, j) => (j === i ? { ...x, ...patch } : x));
  const saveLabel = jobId ? 'Save changes' : 'Save job';

  return (
    <>
      <PageHeader
        back={jobId ? { to: `/jobs/${jobId}`, label: form.title } : { to: '/jobs/new', label: 'Back to your requirements' }}
        eyebrow={jobId ? 'Edit job' : 'New job · step 3 of 3'}
        title={jobId ? form.title : `Review: ${form.title}`}
        subtitle="What the AI asks, how it judges, and what counts as qualified."
        actions={
          <Button variant="primary" icon="check" onClick={save} loading={saving}>
            {saveLabel}
          </Button>
        }
      />

      {!jobId && drafted && (
        <Callout tone="brand" icon="sparkles" className="mb-6">
          <strong className="font-semibold">
            {drafted.source === 'ai' ? `Drafted by AI from your requirements${drafted.roleTitle ? ` and the ${drafted.roleTitle} template` : ''}.` : `Built from the ${drafted.roleTitle ?? 'generic'} template and your requirements.`}
          </strong>{' '}
          Read it through and change anything that does not fit: the competencies, the rules, and the skills-assessment tasks further down. Nothing reaches candidates until you save the job and set it to active.
        </Callout>
      )}
      <ErrorNote error={error} className="mb-6" />

      <div className="grid gap-8 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav className="hidden lg:block" aria-label="Sections">
          <ol className="sticky top-24 space-y-0.5">
            {SECTIONS.map((s, i) => {
              const on = active === s.id;
              return (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={cx('flex items-center gap-2.5 rounded-sm px-3 py-1.5 text-[13px] font-medium transition', on ? 'bg-surface text-fg shadow-card ring-1 ring-border dark:bg-white/8' : 'text-muted-fg hover:text-fg')}
                  >
                    <span className={cx('font-mono text-[10px]', on ? 'text-accent' : 'text-faint')}>{String(i + 1).padStart(2, '0')}</span>
                    {s.label}
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="min-w-0 space-y-5">
          <Section id="role" n={1} title="Role" description="What the job is, and what the interviewer may tell candidates about it.">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Job title">
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
              </Field>
              <Field label="Department" optional>
                <Input value={form.department ?? ''} onChange={(e) => set('department', e.target.value || null)} />
              </Field>
              <Field label="Location">
                <Input value={form.location} leading={<Icon name="pin" className="h-4 w-4" />} onChange={(e) => set('location', e.target.value)} />
              </Field>
              <Field label="Work mode" as="div">
                <Segmented
                  label="Work mode"
                  size="md"
                  value={form.workMode}
                  onChange={(v) => set('workMode', v)}
                  options={[
                    { value: 'On-site', label: 'On-site' },
                    { value: 'Hybrid', label: 'Hybrid' },
                    { value: 'Remote', label: 'Remote' },
                  ]}
                />
              </Field>
              <Field label="About the role" hint="The interviewer answers candidates' questions from this text only." className="md:col-span-2">
                <Textarea rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} />
              </Field>
              <Field label="Status" hint="Interviews can only start while the job is active." as="div">
                <Segmented
                  label="Status"
                  size="md"
                  value={form.status}
                  onChange={(v) => set('status', v)}
                  options={[
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'ACTIVE', label: 'Active', icon: 'zap' },
                    { value: 'CLOSED', label: 'Closed' },
                  ]}
                />
              </Field>
            </div>
          </Section>

          <Section id="outreach" n={2} title="Outreach" description="How candidates are reached, and how long the AI keeps trying.">
            <Field label="How candidates are interviewed" as="div" className="mb-5">
              <Options
                label="How candidates are interviewed"
                value={form.outreachMode}
                onChange={(v) => set('outreachMode', v)}
                options={[
                  { value: 'WEB_LINK', label: 'Web link', icon: 'globe', hint: 'Email a link; the candidate takes a voice interview in the browser.' },
                  { value: 'PHONE_CALL', label: 'Phone call', icon: 'phone', hint: 'The AI calls the candidate within calling hours and retries if unanswered.' },
                  { value: 'PHONE_THEN_WEB', label: 'Phone, then web link', icon: 'send', hint: 'Call first; after the last unanswered attempt, email the link.' },
                ]}
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              <Field label="Interview length" as="div">
                <NumberField value={form.interviewMinutes} min={5} max={45} suffix="min" onChange={(v) => set('interviewMinutes', v)} aria-label="Interview length in minutes" />
              </Field>
              <Field label="Call attempts" hint="Before giving up or falling back to the link." as="div">
                <NumberField value={form.maxCallAttempts} min={1} max={6} onChange={(v) => set('maxCallAttempts', v)} aria-label="Call attempts" />
              </Field>
              <Field label="Retry after" as="div">
                <NumberField value={form.retryAfterMinutes} min={15} max={1440} step={15} suffix="min" onChange={(v) => set('retryAfterMinutes', v)} aria-label="Retry after minutes" />
              </Field>
              <Field label="Link valid for" as="div">
                <NumberField value={form.linkValidityHours} min={1} max={720} suffix="hours" onChange={(v) => set('linkValidityHours', v)} aria-label="Link validity in hours" />
              </Field>
            </div>

            <div className="mt-6 rounded-md bg-surface-2 p-4 ring-1 ring-border">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-fg">Public apply page</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-fg">
                    A page anyone can apply on, with a resume. Share the link on job boards, posters or WhatsApp; applicants are cleaned and de-duplicated like an imported sheet
                    {form.outreachMode === 'WEB_LINK' ? ', and can start their interview the moment they apply.' : ', and the AI calls them within calling hours.'}
                  </p>
                </div>
                <Switch checked={apply.enabled} onChange={(enabled) => setApply({ enabled })} label="Public apply page" />
              </div>
              {apply.enabled && (
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Checkbox className="md:col-span-2" checked={apply.autoStart} onChange={(autoStart) => setApply({ autoStart })} hint="Off: applicants wait as New until a recruiter starts their interview.">
                    Interview applicants as soon as they apply
                  </Checkbox>
                  <Field label="A line for the page" optional hint="Shown under the role description: a salary range, a shift pattern, a deadline." className="md:col-span-2">
                    <Textarea rows={2} value={apply.note ?? ''} onChange={(e) => setApply({ note: e.target.value || undefined })} />
                  </Field>
                  <Field label="The link" hint={applyLink ? 'Anyone with it can apply. “New link” retires it; save to make the new one live.' : jobId ? 'Save the job to get the link.' : 'The link appears once the job is saved.'} className="md:col-span-2" as="div">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input readOnly value={applyLink ?? ''} placeholder="Not live yet" className="min-w-0 flex-1 font-mono !text-xs" leading={<Icon name="link" className="h-4 w-4" />} />
                      {applyLink && <CopyButton text={applyLink} size="md" variant="secondary">Copy</CopyButton>}
                      <Button size="md" variant="ghost" icon="refresh" onClick={() => setApply({ version: apply.version + 1 })}>
                        New link
                      </Button>
                    </div>
                  </Field>
                </div>
              )}
            </div>
          </Section>

          <Section id="interviewer" n={3} title="Interviewer" description="Who the AI says it is, which languages it offers, and the engine behind its voice.">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Company name" hint="As spoken by the interviewer.">
                <Input value={cfg.companyName} leading={<Icon name="building" className="h-4 w-4" />} onChange={(e) => setCfg('companyName', e.target.value)} />
              </Field>
              <Field label="Interviewer name">
                <Input value={cfg.interviewerName} leading={<Icon name="user" className="h-4 w-4" />} onChange={(e) => setCfg('interviewerName', e.target.value)} />
              </Field>
              <Field label="Languages the candidate can choose" hint="The first one ticked is the default." className="md:col-span-2" as="div">
                <div className="flex flex-wrap gap-2">
                  {LANGUAGE_CODES.map((code) => {
                    const on = cfg.languages.includes(code);
                    return (
                      <Chip
                        key={code}
                        on={on}
                        onClick={() => {
                          const next = on ? cfg.languages.filter((l) => l !== code) : [...cfg.languages, code];
                          if (next.length) setCfg('languages', next as [LanguageCode, ...LanguageCode[]]);
                        }}
                      >
                        {LANGUAGES[code].label}
                      </Chip>
                    );
                  })}
                </div>
              </Field>
              <Checkbox className="md:col-span-2" checked={cfg.assessEnglish} onChange={(v) => setCfg('assessEnglish', v)} hint="Scenario questions are asked in English even if the candidate chose another language (customer-facing roles).">
                Assess spoken English
              </Checkbox>
              <Field label="Minimum experience on the sheet" hint="Pre-screen flag only; never an automatic rejection." as="div">
                <NumberField value={cfg.experienceRange?.min ?? 0} min={0} max={40} step={0.5} suffix="years" onChange={(v) => setCfg('experienceRange', { min: v, max: cfg.experienceRange?.max ?? 60 })} aria-label="Minimum experience" />
              </Field>
              <Field label="Maximum experience on the sheet" as="div">
                <NumberField value={cfg.experienceRange?.max ?? 60} min={0} max={60} step={0.5} suffix="years" onChange={(v) => setCfg('experienceRange', { min: cfg.experienceRange?.min ?? 0, max: v })} aria-label="Maximum experience" />
              </Field>
              <Field label="Voice engine" className="md:col-span-2" as="div">
                <Options
                  label="Voice engine"
                  value={cfg.voiceEngine ?? 'standard'}
                  onChange={(v) => setCfg('voiceEngine', v as typeof cfg.voiceEngine)}
                  columns={2}
                  options={[
                    {
                      value: 'standard',
                      label: 'Standard',
                      icon: 'headphones',
                      badge: (
                        <Badge tone="green" size="xs">
                          recommended
                        </Badge>
                      ),
                      hint: 'Sarvam voice and speech recognition around Gemini. Replies in about 2 s; reliable on real phone calls.',
                    },
                    {
                      value: 'gemini-live',
                      label: 'Gemini Live',
                      icon: 'zap',
                      badge: (
                        <Badge tone="amber" size="xs">
                          experimental
                        </Badge>
                      ),
                      hint: 'One Google model hears and speaks: replies in about 1 s, but sometimes stays silent after an answer and costs more. Try it on a test job only.',
                    },
                  ]}
                />
              </Field>
              {usesLive && (
                <Field label="Voice (Gemini Live)" hint="Also the assessor's voice in the skills assessment. Leda is the slowest; Kore the quickest." className="md:col-span-2">
                  <Select value={cfg.liveVoice ?? DEFAULT_LIVE_VOICE} onChange={(e) => setCfg('liveVoice', e.target.value)}>
                    {LIVE_VOICES.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="Extra instructions for the interviewer" optional hint="E.g. how to talk about the salary band or shift timings." className="md:col-span-2">
                <Textarea rows={3} value={cfg.extraInstructions ?? ''} onChange={(e) => setCfg('extraInstructions', e.target.value || undefined)} />
              </Field>
            </div>
          </Section>

          <AssessmentCard
            n={4}
            value={cfg.assessment}
            onChange={(a) => setCfg('assessment', a)}
            context={{ title: form.title, companyName: cfg.companyName, description: form.description, competencies: cfg.competencies, customerFacing: cfg.assessEnglish }}
          />

          <Section
            id="competencies"
            n={5}
            title="Competencies"
            description="Each is judged 1 to 5 by the AI with quoted evidence. The weights say how much each counts in the score."
            actions={
              <Button size="sm" icon="plus" onClick={() => setCfg('competencies', [...cfg.competencies, { key: `skill_${cfg.competencies.length + 1}`, label: 'New skill', description: 'What good looks like for this skill.', weight: 10 }])}>
                Add
              </Button>
            }
          >
            <div className="mb-5 rounded-md bg-surface-2 p-4 ring-1 ring-border">
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="font-semibold text-fg">Weight</span>
                <span className={cx('font-mono', totalWeight === 100 ? 'text-success' : 'text-warning')}>
                  {totalWeight} of 100{totalWeight !== 100 && ' · scores are normalised'}
                </span>
              </div>
              <div className="flex h-3 w-full gap-px overflow-hidden rounded-full bg-muted" role="img" aria-label={cfg.competencies.map((c) => `${c.label} ${c.weight}`).join(', ')}>
                {cfg.competencies.map((c, i) => (
                  <span key={c.key + i} className="h-full transition-[width] duration-300" style={{ width: `${(Math.max(0, Number(c.weight) || 0) / Math.max(100, totalWeight)) * 100}%`, background: RAMP[i % RAMP.length] }} title={`${c.label}: ${c.weight}`} />
                ))}
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-fg">
                {cfg.competencies.map((c, i) => (
                  <li key={c.key + i} className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: RAMP[i % RAMP.length] }} />
                    {c.label || 'Untitled'} <span className="font-mono">{c.weight}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              {cfg.competencies.map((c, i) => (
                <ItemCard
                  key={i}
                  title={
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: RAMP[i % RAMP.length] }} />
                      {c.label || 'New competency'}
                    </span>
                  }
                  onRemove={cfg.competencies.length > 1 ? () => setCfg('competencies', cfg.competencies.filter((_, j) => j !== i)) : undefined}
                >
                  <div className="grid gap-4 md:grid-cols-6">
                    <Field label="Name" className="md:col-span-4">
                      <Input value={c.label} onChange={(e) => setCfg('competencies', updateList<Competency>(cfg.competencies, i, { label: e.target.value, key: jobId ? c.key : toKey(e.target.value) }))} />
                    </Field>
                    <Field label="Weight" className="md:col-span-2" as="div">
                      <NumberField value={c.weight} min={0} max={100} suffix="%" onChange={(v) => setCfg('competencies', updateList<Competency>(cfg.competencies, i, { weight: v }))} aria-label={`Weight of ${c.label}`} />
                    </Field>
                    <Field label="What good looks like" className="md:col-span-6">
                      <Textarea rows={2} value={c.description} onChange={(e) => setCfg('competencies', updateList<Competency>(cfg.competencies, i, { description: e.target.value }))} />
                    </Field>
                    <Field label="Scenario or opening question" optional className="md:col-span-6">
                      <Textarea rows={2} value={c.probe ?? ''} onChange={(e) => setCfg('competencies', updateList<Competency>(cfg.competencies, i, { probe: e.target.value || undefined }))} />
                    </Field>
                  </div>
                </ItemCard>
              ))}
            </div>
          </Section>

          <Section
            id="facts"
            n={6}
            title="Facts to establish"
            description="Extracted from the conversation; the qualification rules below are applied to them."
            actions={
              <Button size="sm" icon="plus" onClick={() => setCfg('facts', [...cfg.facts, { key: `fact_${cfg.facts.length + 1}`, label: 'New fact', type: 'text', ask: 'How to ask for it.', required: false }])}>
                Add
              </Button>
            }
          >
            <div className="space-y-3">
              {cfg.facts.map((f, i) => (
                <ItemCard key={i} onRemove={() => setCfg('facts', cfg.facts.filter((_, j) => j !== i))} title={f.label || 'New fact'}>
                  <div className="grid gap-4 md:grid-cols-12">
                    <Field label="Fact" className="md:col-span-3">
                      <Input value={f.label} onChange={(e) => setCfg('facts', updateList<Fact>(cfg.facts, i, { label: e.target.value, key: jobId ? f.key : toKey(e.target.value) }))} />
                    </Field>
                    <Field label="Type" className="md:col-span-2">
                      <Select value={f.type} onChange={(e) => setCfg('facts', updateList<Fact>(cfg.facts, i, { type: e.target.value as Fact['type'] }))}>
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="boolean">Yes / no</option>
                      </Select>
                    </Field>
                    <Field label="Unit" optional className="md:col-span-2">
                      <Input value={f.unit ?? ''} placeholder="days, LPA…" onChange={(e) => setCfg('facts', updateList<Fact>(cfg.facts, i, { unit: e.target.value || undefined }))} />
                    </Field>
                    <Field label="How to ask" className="md:col-span-5">
                      <Input value={f.ask} onChange={(e) => setCfg('facts', updateList<Fact>(cfg.facts, i, { ask: e.target.value }))} />
                    </Field>
                    <Checkbox className="md:col-span-12" checked={f.required} onChange={(v) => setCfg('facts', updateList<Fact>(cfg.facts, i, { required: v }))} hint="The interviewer makes sure to get it before closing.">
                      Required
                    </Checkbox>
                  </div>
                </ItemCard>
              ))}
            </div>
          </Section>

          <Section
            id="rules"
            n={7}
            title="Qualification rules"
            description="Applied by code, never by the AI. A hard rule fails the candidate outright; a soft rule sends an otherwise qualified candidate to review."
            actions={
              <Button size="sm" icon="plus" disabled={cfg.facts.length === 0} onClick={() => setCfg('knockouts', [...cfg.knockouts, { fact: cfg.facts[0]!.key, op: 'gt', value: 0, severity: 'soft', message: 'Describe the problem' }])}>
                Add rule
              </Button>
            }
          >
            <div className="mb-6 rounded-md bg-surface-2 p-4 ring-1 ring-border">
              <p className="mb-3 text-xs font-semibold text-fg">Score bands</p>
              <ThresholdBar qualify={cfg.thresholds.qualify} review={cfg.thresholds.review} />
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Qualified from" hint="0-100, weighted from the competency scores." as="div">
                  <NumberField value={cfg.thresholds.qualify} min={0} max={100} onChange={(v) => setCfg('thresholds', { ...cfg.thresholds, qualify: v })} aria-label="Qualify at score" />
                </Field>
                <Field label="Not qualified below" hint="Between the two goes to human review." as="div">
                  <NumberField value={cfg.thresholds.review} min={0} max={100} onChange={(v) => setCfg('thresholds', { ...cfg.thresholds, review: v })} aria-label="Review threshold" />
                </Field>
              </div>
            </div>
            {cfg.knockouts.length === 0 && <p className="text-sm text-muted-fg">No rules yet. Add one to reject or flag candidates on a fact, for example a notice period over 60 days.</p>}
            <div className="space-y-3">
              {cfg.knockouts.map((k, i) => {
                const fact = cfg.facts.find((f) => f.key === k.fact);
                const op = OPS.find((o) => o.value === k.op)!;
                return (
                  <ItemCard key={i} onRemove={() => setCfg('knockouts', cfg.knockouts.filter((_, j) => j !== i))} title={<span className="inline-flex items-center gap-2"><Badge tone={k.severity === 'hard' ? 'red' : 'amber'} size="xs">{k.severity}</Badge> Rule {i + 1}</span>}>
                    <div className="grid items-end gap-3 md:grid-cols-12">
                      <Field label="If" className="md:col-span-3">
                        <Select value={k.fact} onChange={(e) => setCfg('knockouts', updateList<KnockoutRule>(cfg.knockouts, i, { fact: e.target.value }))}>
                          {cfg.facts.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Condition" className="md:col-span-3">
                        <Select value={k.op} onChange={(e) => setCfg('knockouts', updateList<KnockoutRule>(cfg.knockouts, i, { op: e.target.value as KnockoutRule['op'] }))}>
                          {OPS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={fact?.unit ?? 'Value'} className="md:col-span-2">
                        <Input
                          disabled={!op.needsValue}
                          value={op.needsValue ? String(k.value ?? '') : ''}
                          onChange={(e) => setCfg('knockouts', updateList<KnockoutRule>(cfg.knockouts, i, { value: fact?.type === 'number' ? Number(e.target.value) : e.target.value }))}
                        />
                      </Field>
                      <Field label="Then" className="md:col-span-4">
                        <Select value={k.severity} onChange={(e) => setCfg('knockouts', updateList<KnockoutRule>(cfg.knockouts, i, { severity: e.target.value as 'hard' | 'soft' }))}>
                          <option value="soft">Send to review (soft)</option>
                          <option value="hard">Not qualified (hard)</option>
                        </Select>
                      </Field>
                      <Field label="Reason shown" hint="On the candidate's card and in the verdict." className="md:col-span-12">
                        <Input value={k.message} onChange={(e) => setCfg('knockouts', updateList<KnockoutRule>(cfg.knockouts, i, { message: e.target.value }))} />
                      </Field>
                    </div>
                  </ItemCard>
                );
              })}
            </div>
          </Section>

          <JobMessagesCard n={8} value={cfg.messages} onChange={(m) => setCfg('messages', m)} creatorEmail={jobId ? undefined : user?.email} />

          <div className="flex justify-end pb-24">
            <Button variant="primary" size="lg" icon="check" onClick={save} loading={saving}>
              {saveLabel}
            </Button>
          </div>
        </div>
      </div>

      {(dirty || !jobId) && (
        <div className="pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center px-4 lg:pl-[264px]">
          <div className="scale-in pointer-events-auto flex items-center gap-3 rounded-xl bg-ink px-4 py-2 text-white shadow-pop ring-1 ring-white/10">
            <span className="flex items-center gap-2 text-sm">
              <span className="live-dot h-2 w-2 rounded-full bg-[#fbbf24]" aria-hidden />
              {jobId ? 'Unsaved changes' : 'Not saved yet'}
            </span>
            <Button variant="primary" size="sm" onClick={save} loading={saving}>
              {saveLabel}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/** The three score bands as one bar: not qualified, review, qualified. */
function ThresholdBar({ qualify, review }: { qualify: number; review: number }) {
  const lo = Math.max(0, Math.min(100, Math.min(review, qualify)));
  const hi = Math.max(0, Math.min(100, Math.max(review, qualify)));
  return (
    <div>
      <div className="flex h-3 w-full gap-px overflow-hidden rounded-full" role="img" aria-label={`Not qualified below ${lo}, review up to ${hi}, qualified from ${hi}`}>
        <span className="h-full bg-destructive/80" style={{ width: `${lo}%` }} />
        <span className="h-full bg-warning/80" style={{ width: `${hi - lo}%` }} />
        <span className="h-full bg-success/80" style={{ width: `${100 - hi}%` }} />
      </div>
      <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-muted-fg">
        <span>0 · not qualified</span>
        <span>{lo} · review</span>
        <span>{hi} · qualified · 100</span>
      </div>
    </div>
  );
}
